import React, { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';
import { Button } from './ui/button';
import { logError } from '@/utils/errorHandling';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  showReportButton?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  eventId?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log to local error handling
    logError(error, {
      component: 'ErrorBoundary',
      errorInfo: errorInfo.componentStack
    });
    
    // Log to Sentry with event ID for user reports
    const eventId = Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      tags: {
        errorBoundary: 'true',
        url: window.location.href,
      },
    });

    this.setState({ errorInfo, eventId });
    
    // Call optional error callback
    this.props.onError?.(error, errorInfo);

    // Log to backend error tracking
    this.logToBackend(error, errorInfo);
  }

  private async logToBackend(error: Error, errorInfo: React.ErrorInfo) {
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      await supabase.from('error_logs').insert({
        error_type: 'react_error_boundary',
        error_message: error.message,
        error_stack: error.stack,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        severity: 'error',
        metadata: {
          componentStack: errorInfo.componentStack,
          timestamp: new Date().toISOString(),
        },
      });
    } catch (logError) {
      console.error('Failed to log error to backend:', logError);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined, eventId: undefined });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReportFeedback = () => {
    if (this.state.eventId) {
      Sentry.showReportDialog({ eventId: this.state.eventId });
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full cosmic-card p-8 text-center">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <AlertTriangle className="w-12 h-12 text-destructive animate-pulse" />
                <div className="absolute -inset-2 bg-destructive/20 rounded-full blur-xl" />
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Something went wrong
            </h2>
            
            <p className="text-muted-foreground mb-6 text-sm">
              We've logged this error and will look into it. Please try one of the options below.
            </p>
            
            <div className="space-y-3">
              <Button 
                onClick={this.handleReset}
                className="w-full"
                variant="default"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              
              <Button 
                onClick={this.handleReload}
                className="w-full"
                variant="secondary"
              >
                Reload Page
              </Button>

              <Button 
                onClick={this.handleGoHome}
                className="w-full"
                variant="outline"
              >
                <Home className="w-4 h-4 mr-2" />
                Go to Homepage
              </Button>

              {this.props.showReportButton && this.state.eventId && (
                <Button 
                  onClick={this.handleReportFeedback}
                  className="w-full"
                  variant="ghost"
                  size="sm"
                >
                  <Bug className="w-4 h-4 mr-2" />
                  Report this issue
                </Button>
              )}
            </div>
            
            {import.meta.env.DEV && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-muted-foreground text-sm hover:text-foreground">
                  Error Details (Development Only)
                </summary>
                <div className="mt-2 space-y-2">
                  <pre className="text-xs bg-destructive/10 border border-destructive/20 p-3 rounded overflow-auto max-h-40">
                    {this.state.error.message}
                  </pre>
                  {this.state.error.stack && (
                    <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-60">
                      {this.state.error.stack}
                    </pre>
                  )}
                  {this.state.errorInfo?.componentStack && (
                    <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-40">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              </details>
            )}

            {this.state.eventId && (
              <p className="mt-4 text-xs text-muted-foreground">
                Error ID: {this.state.eventId.slice(0, 8)}
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Simple error boundary for smaller components
export const SimpleErrorBoundary = ({ children, fallback }: Props) => (
  <ErrorBoundary fallback={
    fallback || (
      <div className="p-4 border border-destructive/20 rounded-lg bg-destructive/5">
        <p className="text-sm text-destructive">Failed to load component</p>
      </div>
    )
  }>
    {children}
  </ErrorBoundary>
);